import { Injectable, OnInit } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { 
  Firestore, 
  collection, 
  doc, 
  runTransaction, 
  query, 
  orderBy, 
  limit, 
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  where,
  limit as limitTo
} from '@angular/fire/firestore';
import { Observable, from, map, of, switchMap, BehaviorSubject } from 'rxjs';
import { PointsTransaction, UserPoints } from '../interfaces/points.interface';

@Injectable({
  providedIn: 'root'
})
export class PointsService implements OnInit {
  private pointsSubject = new BehaviorSubject<UserPoints | null>(null);
  points$ = this.pointsSubject.asObservable();

  constructor(private auth: Auth, private firestore: Firestore) {}

  ngOnInit() {
    this.initializePoints();
  }

  private async initializePoints() {
    this.auth.onAuthStateChanged(async user => {
      if (user) {
        const pointsRef = doc(this.firestore, `points/${user.uid}`);
        const pointsDoc = await getDoc(pointsRef);
        if (!pointsDoc.exists()) {
          await setDoc(pointsRef, {
            userId: user.uid,
            balance: 0,
            lastUpdated: new Date()
          });
        }
        // Update the subject with initial points
        await this.updatePoints(user.uid);
      } else {
        this.pointsSubject.next(null);
      }
    });
  }

  private async updatePoints(userId: string) {
    const pointsRef = doc(this.firestore, `points/${userId}`);
    const pointsDoc = await getDoc(pointsRef);
    if (pointsDoc.exists()) {
      this.pointsSubject.next({ userId, ...pointsDoc.data() } as UserPoints);
    }
  }

  async addTransaction(amount: number, type: 'earn' | 'spend', description: string): Promise<void> {
    // Get the current authenticated user
    const user = this.auth.currentUser;
    if (!user) {
      console.error('No authenticated user found');
      throw new Error('You must be logged in to add a transaction');
    }
    
    console.log(`addTransaction: Processing ${type} transaction for ${amount} points`);
    console.log(`addTransaction: Current user: ${user.uid}`);
    console.log(`addTransaction: Transaction description: ${description}`);
    
    // Reference to the user's points document
    const pointsRef = doc(this.firestore, `points/${user.uid}`);
    console.log(`addTransaction: Checking points document at: points/${user.uid}`);
    
    // Check if the points document exists
    const pointsDocSnapshot = await getDoc(pointsRef);
    
    // If the document doesn't exist, create it with an initial balance
    if (!pointsDocSnapshot.exists()) {
      console.log(`addTransaction: Creating new points document for user ${user.uid} with initial balance of 10`);
      await setDoc(pointsRef, {
        balance: 10, // Initial balance
        lastUpdated: new Date()
      });
    }
    
    // Now run the transaction with the document that should exist
    await runTransaction(this.firestore, async (transaction) => {
      const pointsDoc = await transaction.get(pointsRef);
      if (!pointsDoc.exists()) {
        // This shouldn't happen now, but just in case
        console.error('addTransaction: Points document still does not exist after creation attempt');
        throw new Error('Points document does not exist');
      }

      const currentPoints = pointsDoc.data()['balance'] || 0;
      const newBalance = type === 'earn' ? currentPoints + amount : currentPoints - amount;
      
      console.log(`addTransaction: Current points balance: ${currentPoints}`);
      console.log(`addTransaction: Transaction amount: ${amount}`);
      console.log(`addTransaction: Transaction type: ${type}`);
      console.log(`addTransaction: New balance would be: ${newBalance}`);

      if (type === 'spend' && newBalance < 0) {
        console.error(`addTransaction: Insufficient points! Current: ${currentPoints}, Trying to spend: ${amount}`);
        throw new Error('Insufficient points');
      }

      // Update points balance
      transaction.update(pointsRef, { 
        balance: newBalance,
        lastUpdated: new Date()
      });

      // Add transaction record
      const transactionsRef = collection(this.firestore, 'transactions');
      transaction.set(doc(transactionsRef), {
        userId: user.uid,
        amount,
        type,
        description,
        timestamp: new Date()
      });
    });

    // Update points after transaction
    await this.updatePoints(user.uid);
  }

  async addPoints(childId: string, amount: number, reason: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const pointsRef = doc(this.firestore, `points/${childId}`);
    const pointsDoc = await getDoc(pointsRef);
    
    // Create points document if it doesn't exist
    if (!pointsDoc.exists()) {
      await setDoc(pointsRef, {
        userId: childId,
        balance: 0,
        lastUpdated: new Date()
      });
    }

    const transactionRef = doc(collection(this.firestore, 'transactions'));

    await setDoc(transactionRef, {
      childId,
      parentId: user.uid,
      amount,
      reason,
      timestamp: new Date()
    });

    await updateDoc(pointsRef, {
      balance: increment(amount)
    });
  }

  getUserPoints(): Observable<UserPoints | null> {
    return this.points$;
  }

  getTransactions(limit = 10): Observable<PointsTransaction[]> {
    return from(Promise.resolve(this.auth.currentUser)).pipe(
      switchMap(user => {
        if (!user) return of([]);
        const transactionsRef = collection(this.firestore, 'transactions');
        const q = query(
          transactionsRef,
          where('userId', '==', user.uid),
          orderBy('timestamp', 'desc'),
          limitTo(limit)
        );
        return from(getDocs(q)).pipe(
          map(snapshot => 
            snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }) as PointsTransaction)
          )
        );
      })
    );
  }
} 